import { DATE_PIPE_DEFAULT_OPTIONS, registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import localeEsMX from '@angular/common/locales/es-MX';
import { ApplicationConfig, ErrorHandler, provideAppInitializer, provideZoneChangeDetection, ApplicationRef, importProvidersFrom, inject, Injector, isDevMode, LOCALE_ID, ViewContainerRef } from '@angular/core';
import { Router } from '@angular/router';
import { createErrorHandler, TraceService } from '@sentry/angular';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { provideApollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { GoogleLoginProvider, SocialAuthServiceConfig } from '@abacritt/angularx-social-login';
import { JwtModule } from '@auth0/angular-jwt';
import { GoogleMapsModule } from '@angular/google-maps';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { ServiceWorkerModule } from '@angular/service-worker';
import { CookieService } from 'ngx-cookie-service';
import { environment } from 'src/environments/environment';

import { tokenInterceptor } from '../shared/interceptors/token/token.interceptor';
import { clientUaInterceptor } from '../shared/interceptors/client-ua/client-ua.interceptor';
import { initClientUserAgent } from '../shared/utils/client-user-agent.util';
import { recordVisitOnce } from '../shared/utils/visit-beacon.util';
import { configureTimezone } from '../shared/config/timezone.config';
import { getAppTimezone } from '../shared/config/app-timezone.config';
import { GlobalConfigService } from '../shared/services/global-config.service';
import { LoaderService } from '../shared/services/loader/loader.service';
import { MaintenanceService } from '../shared/services/maintenance/maintenance.service';
import { TimezoneService } from '../shared/services/date/timezone.service';
import { SharedModule } from '../shared/shared.module';
import { ROOT_REDUCERS } from '../shared/states/app.state';
import { createApollo } from './graphql.module';
import { routes } from './app.routes';
import { isSentryEnabled } from '../shared/config/sentry.config';

configureTimezone();
registerLocaleData(localeEsMX, 'es-MX');

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    // clientUaInterceptor va después del de tokens: necesita ver el Authorization ya puesto.
    provideHttpClient(withInterceptors([tokenInterceptor, clientUaInterceptor])),
    // Calcula al arrancar el User-Agent que se reporta al backend (con el modelo real en
    // Chrome para Android) y, con ese dato, cuenta la visita para el panel de Soporte
    // (también sin sesión). Sin `await`: no demora el inicio de la app.
    provideAppInitializer(() => {
      void initClientUserAgent().then(() => recordVisitOnce());
    }),
    provideAnimations(),
    // Modo mantenimiento: revisa cada minuto mientras la app está abierta, para mandar a
    // la pantalla a quien ya estaba adentro cuando se activa (salvo compra en curso).
    provideAppInitializer(() => {
      inject(MaintenanceService).startWatching();
    }),
    provideApollo(() => createApollo(inject(HttpLink), inject(Injector))),
    importProvidersFrom(
      JwtModule.forRoot({
        config: {
          allowedDomains: ['sarkosgroup.com'],
          tokenGetter: () => JSON.parse(localStorage.getItem('sessionAuthState') || ''),
        },
      }),
      GoogleMapsModule,
      SharedModule.forRoot(),
      StoreModule.forRoot(ROOT_REDUCERS),
      StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: !isDevMode() }),
      ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.production })
    ),
    CookieService,
    {
      provide: 'SocialAuthServiceConfig',
      useValue: {
        autoLogin: false,
        providers: [
          {
            id: GoogleLoginProvider.PROVIDER_ID,
            provider: new GoogleLoginProvider(
              '847047958793-2bellvk4avv60j6dcdc12gcbetaka15q.apps.googleusercontent.com',
              {
                scopes: 'profile email',
                oneTapEnabled: false,
              }
            ),
          },
        ],
      } as SocialAuthServiceConfig,
    },
    {
      provide: 'environment',
      useValue: environment,
    },
    {
      provide: 'APP_VIEW_CONTAINER_REF',
      useFactory: (appRef: ApplicationRef) => appRef.components[0].injector.get(ViewContainerRef),
      deps: [ApplicationRef],
    },
    LoaderService,
    TimezoneService,
    GlobalConfigService,
    { provide: LOCALE_ID, useValue: 'es-MX' },
    { provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { timezone: getAppTimezone() } },
    ...(isSentryEnabled()
      ? [
          { provide: ErrorHandler, useValue: createErrorHandler({ showDialog: false }) },
          { provide: TraceService, deps: [Router] },
          provideAppInitializer(() => {
            inject(TraceService);
          }),
        ]
      : []),
  ],
};
